import {
  Anchor,
  Box,
  Center,
  Group,
  Skeleton,
  Stack,
  Text,
  Timeline,
  Title,
  useMatches,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EmptyStateRevamped, PlaceholderValue, TextSerif } from "@/components/elements";
import { AuctionCarHistory } from "@/types/auctions";

import type { FC } from "react";

export type CarHistoryProps = {
  history?: AuctionCarHistory[];
  loading?: boolean;
};

type RenderItemKey = keyof AuctionCarHistory;

export const CarHistory: FC<CarHistoryProps> = ({ history = [], loading = false }) => {
  const { t } = useTranslation();

  const isMobile = useMatches({ base: true, md: false });

  function renderItem(key: RenderItemKey, item: AuctionCarHistory) {
    if (item[key]) {
      const value = item[key];

      switch (key) {
        case "date":
        case "name":
        case "event_website":
          return null;
        case "description":
          return <Text key={key}>{value}</Text>;
        case "event":
          if (item.event_website) {
            return (
              <Group key={key} align="baseline" gap="sm">
                <TextSerif>{t(`components.CarHistory.labels.${key}`, key)}:</TextSerif>
                <Anchor
                  c="inherit"
                  href={item.event_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="not-hover"
                >
                  {value}
                </Anchor>
              </Group>
            );
          }
        default:
          return (
            <Group key={key} align="baseline" gap="sm">
              <TextSerif>{t(`components.CarHistory.labels.${key}`, key)}:</TextSerif>
              <Text>{value}</Text>
            </Group>
          );
      }
    }

    return null;
  }

  return (
    <Stack>
      <Title order={3} mb="lg">
        {t("components.CarHistory.title")}
      </Title>
      <Skeleton visible={loading}>
        {history.length > 0 ? (
          <>
            <Timeline lineWidth={3}>
              {history.map((item, index) => (
                <Timeline.Item
                  key={index}
                  title={
                    <TextSerif mb="sm" c="gray" title={item.date}>
                      {item.name}
                    </TextSerif>
                  }
                >
                  <Stack gap="xs">
                    {(Object.keys(item) as RenderItemKey[]).map((key) => renderItem(key, item))}
                  </Stack>
                </Timeline.Item>
              ))}
            </Timeline>
          </>
        ) : (
          <>
            <Box pos="relative">
              <Timeline lineWidth={3} opacity={0.3}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Timeline.Item key={index} title={<PlaceholderValue />}>
                    <Stack gap="xs">
                      <PlaceholderValue />
                    </Stack>
                  </Timeline.Item>
                ))}
              </Timeline>

              <Center
                pos="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                h={isMobile ? "16em" : "18em"}
              >
                <EmptyStateRevamped
                  title={t("components.CarHistory.title")}
                  description={t("components.CarHistory.description")}
                />
              </Center>
            </Box>
          </>
        )}
      </Skeleton>
    </Stack>
  );
};
